import type React from "react";
import { useState, useEffect } from "react";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import styles from "../devotee.module.scss";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import constants from "../../../config/constants.json";
import LOCALE from "../../../config/en-us.json";
import {
  Grid,
  Typography,
  FormControl,
  Box,
  Card,
  InputAdornment,
} from "@mui/material";
import AddMoreDonations from "./AddMoreDonations";
import { ValidationError, object, string } from "yup";
import toast from "react-hot-toast";
import {
  apiRequest,
  authService,
  catchErrorMessageHandler,
  getUserName,
} from "../../../infrastructure/backendService";
import {
  CustomSelect,
  CustomMenuItem,
  CustomRequiredInputLabel,
  CustomTextFeild,
  CustomInputLabel,
  CommonButton,
  HcccCard,
} from "../../common/StyledComponents/StyledFields";
import apiPath from "../../../config/apiPaths.json";
import { useNavigate } from "react-router";
import { isNil } from "lodash";
import DevoteeModeOfPayment from "../../common/modeOfPayment/devoteeModeOfPayment";
import ErrorPopup from "../../common/ErrorPopup";
import { useSearchParams } from "react-router-dom";

interface DonationProps {
  amount?: string;
}

interface ErrorMessageTypes {
  name: string;
  address: string;
  donationPurpose: string;
  amount: string;
}

const Donation: React.FC = (props: DonationProps) => {
  const [searchParam] = useSearchParams();
  const donationID = searchParam.get("donationID")

  const [isOpen, setIsOpen] = useState<boolean>(false);
  // Define form state using the useState hook
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    donationPurpose: "",
    amount: "",
    modeOfPayments: "",
  });
  const [errorMessage, setErrorMessage] = useState<ErrorMessageTypes>({
    name: "",
    address: "",
    donationPurpose: "",
    amount: "",
  });

  const [isAddMoreOpen, setIsAddMoreOpen] = useState(false);
  const [donationPurposeItems, setDonationPurposeItems] = useState<any>([]);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState("");
  const [buttonDisable, setButtonDisable] = useState(true);

  const handleSetInitialFormData = (userData: any) => {
    let donationPurpose = "";

    if (!isNil(donationID)) {
      const selectedItem = donationPurposeItems?.find(
        (item: any) => item.accountNumber === donationID
      );

      // If the item is found, extract the accountNumber
      if (selectedItem) {
        donationPurpose = donationID;
        setErrorMessage({ ...errorMessage, donationPurpose: "" });
      }
    }

    setFormData({
      ...formData,
      address: userData?.address,
      name: `${userData?.firstname} ${userData?.lastname}`,
      donationPurpose: donationPurpose,
    });
  };

  useEffect(() => {
    if (donationPurposeItems?.length > 0) {
      apiRequest({
        method: "GET",
        endpoint: `${
          apiPath.getDevoteeDetails
        }/${getUserName()?.toUpperCase()}`,
        includeToken: true,
      })
        .then((res) => {
          const userFromApi = res.data;
          handleSetInitialFormData(userFromApi);
        })
        .catch((err) => {
          console.log(err);
          toast.custom(
            <ErrorPopup
              showMessage={catchErrorMessageHandler(err)}
              isOpen={isOpen}
              setIsOpen={setIsOpen}
            />,
            {
              duration: constants.TOAST_POPUP_DURATION,
            }
          );
        });
    }
  }, [donationPurposeItems]);

  const toggleAddMore = (): void => {
    setIsAddMoreOpen(!isAddMoreOpen);
  };
  const handleFieldChange = (field: any, value: any): any => {
    if (field === "donationPurpose") {
      sessionStorage.setItem("donationPurposeID", value);

      setFormData({
        ...formData,
        [field]: value,
      });
    } else {
      setFormData({
        ...formData,
        [field]: value,
      });
    }
  };

  const validationSchema: any = object().shape({
    donationPurpose: string().required("Dropdown is required"),
    amount: string().required("Amount is required"),
  });

  useEffect(() => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.purposeOfDonations,
    })
      .then((res) => {
        setDonationPurposeItems(res.data);
      })
      .catch((err) => {
        console.log(err);
        toast.custom(<ErrorPopup showMessage={catchErrorMessageHandler(err)} isOpen={isOpen} setIsOpen={setIsOpen} />, {
          duration: constants.TOAST_POPUP_DURATION,
        });
      });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 180); // Scroll to the top of the page
    // Find the specific heading element by its ID or class name
    const donations = document.getElementById("donations");
    if (donations) {
      donations.scrollIntoView({ behavior: "smooth" }); // Scroll to the heading
    }
  }, []);

  const DonationDropdown = (): React.ReactElement => {
    return (
      <FormControl className={styles.donationDropdownMainContainer}>
        <CustomSelect
          defaultValue=""
          value={formData.donationPurpose}
          id="purposeOfDonations"
          IconComponent={KeyboardArrowDownIcon}
          onChange={(e) => {
            setErrorMessage({ ...errorMessage, donationPurpose: "" });
            handleFieldChange("donationPurpose", e.target.value);
          }}
          className={
            errorMessage.donationPurpose
              ? styles.errorTextField
              : styles.textField
          }
          MenuProps={{
            PaperProps: {
              sx: {
                border: "1px solid #F05802",
                "& ul": {
                  paddingTop: 0,
                  paddingBottom: 0,
                  overflowY: "auto",
                  minHeight: "auto",
                  maxHeight: "300px",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#F3C539",
                    borderRadius: "6px",
                  },
                },
              },
            },
          }}
        >
          {donationPurposeItems.map((item: any, index: number) => (
            <CustomMenuItem key={index} value={item.accountNumber}>
              {item.accountDescription}
            </CustomMenuItem>
          ))}
        </CustomSelect>
        {errorMessage.donationPurpose && errorMessage.donationPurpose && (
          <Typography sx={{ color: "red", fontSize: "12px" }}>
            {errorMessage.donationPurpose}
          </Typography>
        )}
      </FormControl>
    );
  };

  const handleClick = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setSelectedPaymentMode("credit card");
      const itemsList = [
        {
          donationpurposeid: formData.donationPurpose,
          amount: formData.amount
        },
      ]
      sessionStorage.setItem('cartItems', JSON.stringify(itemsList));
      navigate(`/creditPayment?amount=${formData.amount}`);
      window.scrollTo(0, 150)
    } catch (error: any) {
      // Handle validation errors
      const errors = {} as ErrorMessageTypes;
      if (error.inner) {
        error.inner.forEach((e: any) => {
          errors[e.path as keyof ErrorMessageTypes] = e.message;
        });
      }
      setErrorMessage(errors);
    }
  };

  return (
    <>
      <Typography
        sx={{
          color: "#414141",
          fontFamily: "Lato",
          fontSize: "32px",
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: " 140%",
          marginBottom: "16px",
        }}
      >
        Donation
      </Typography>
      <HcccCard className={styles.WholeConatiner}>
        <Box className={styles.BoxContainer}>
          <form className={styles.formContainer} onSubmit={handleClick}>
            <Typography className={styles.typoGraphySubheading}>
              Details
            </Typography>

            <Grid className={styles.singlefeild}>
              {CustomRequiredInputLabel("Name", true)}
              <CustomTextFeild
                id="name"
                required
                value={formData.name}
                onChange={(e) => {
                  setErrorMessage({ ...errorMessage, name: "" });
                  handleFieldChange("name", e.target.value);
                }}
                className={
                  errorMessage.name ? styles.errorTextField : styles.textField
                }
                InputProps={{
                  readOnly: true,
                }}
              />

              {errorMessage.name && (
                <Typography sx={{ color: "red", fontSize: "12px" }}>
                  {errorMessage.name}
                </Typography>
              )}
            </Grid>
            <Grid className={styles.singlefeild}>
              <CustomInputLabel className={styles.labels}>
                Address
              </CustomInputLabel>
              <CustomTextFeild
                id="address"
                required
                value={formData.address}
                onChange={(e) => {
                  // setErrorMessage({ ...errorMessage, address: '' });
                  handleFieldChange("address", e.target.value);
                }}
                className={
                  errorMessage.address
                    ? styles.errorTextField
                    : styles.textField
                }
                InputProps={{
                  readOnly: true,
                }}
              />
              {errorMessage.address && (
                <Typography sx={{ color: "red", fontSize: "12px" }}>
                  {errorMessage.address}
                </Typography>
              )}
            </Grid>

            <Grid className={styles.donationDropdownAndButton}>
              <Box className={styles.dropdownLabel}>
                {CustomRequiredInputLabel("Donation Purpose", true)}
                {DonationDropdown()}
              </Box>

              {/* <CommonButton
                onClick={toggleAddMore}
                disabled={!formData.donationPurpose}
                className={styles.addListButton}
              >
                Add to List
              </CommonButton> */}
            </Grid>
            <Grid className={styles.singlefeild}>
              {CustomRequiredInputLabel("Suggested Donation Amount", true)}
              <CustomTextFeild
                sx={{ width: "calc(100% - 30px)" }}
                required
                id="donationAmount"
                type="number"
                value={formData.amount}
                onChange={(e) => {
                  setErrorMessage({ ...errorMessage, amount: "" });
                  let input = e.target.value.replace(/[^0-9.]/g, ""); // Remove non-digit characters
                  // Disallow single '0'
                  if (input === "0") {
                    input = "";
                  }
                  const limitedInput = input.slice(0, 10); // Limit to 10 digits
                  handleFieldChange("amount", limitedInput);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon
                        fontSize="small"
                        sx={{ position: "absolute" }}
                      />
                    </InputAdornment>
                  ),
                }}
                className={
                  errorMessage.amount ? styles.errorTextField : styles.textField
                }
                placeholder="Enter Donation Amount"
              />
              {errorMessage.amount && (
                <Typography sx={{ color: "red", fontSize: "12px" }}>
                  {errorMessage.amount}
                </Typography>
              )}
            </Grid>
            <Grid xs={12} sm={12} item className={styles.singleFeild}>
              <Typography
                style={{
                  marginTop:"10px",
                  color: "red",
                  background: "yellow",
                  fontFamily: "Lato",
                  fontStyle: "normal",
                  fontSize: "14px",
                  width:"fit-content"
                }}
              >
                {LOCALE.DONATION_AMOUNT_SUGGESTION}
              </Typography>
            </Grid>

            <DevoteeModeOfPayment
              setButtonDisable={setButtonDisable}
              setSelectedPaymentMode={setSelectedPaymentMode}
              handleClick={handleClick}
              amount={parseFloat(formData.amount)}
              donationPurpose={formData.donationPurpose}
            />

            {!buttonDisable ? (
              <CommonButton
                sx={{ margin: " 16px 0" }}
                disabled={buttonDisable}
                onClick={handleClick}
                id="proceed"
                type="submit"
              >
                Proceed
              </CommonButton>
            ) : (
              ""
            )}

            {/* <Box className={styles.reccuringPayment}>
                     <CustomInputLabel>Recurring Payment</CustomInputLabel>

                     <Box className={styles.CheckBoxStyles}>
                        <FormControlLabel
                           control={
                              <Checkbox
                                 defaultChecked
                                 size="small"
                                 className={styles.singlecheckBox}
                              />
                           }
                           label="Weekly"
                        />
                        <FormControlLabel
                           control={<Checkbox size="small" className={styles.singlecheckBox} />}
                           label="Monthly"
                        />
                        <FormControlLabel
                           control={<Checkbox size="small" className={styles.singlecheckBox} />}
                           label="Yearly"
                        />
                     </Box>
                  </Box> */}
          </form>
        </Box>

        {isAddMoreOpen && (
          <Box sx={{ width: "1500px" }}>
            <AddMoreDonations />
          </Box>
        )}
      </HcccCard>
    </>
  );
};

export default Donation;
